import {Box, Typography, Chip} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import './ArrFilterChip.scss';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import CloseIcon from '@mui/icons-material/Close';
import {isArray} from 'lodash';
import {ChevronLeft, ChevronRight} from '@mui/icons-material';
import {t} from 'i18next';
import clsx from 'clsx';

export interface Selection {
  id: number | string;
  value: string;
}

interface FilterChipProps {
  id?: string;
  filterName: string | string[];
  selections: Selection[];
  operator?: 'AND' | 'OR' | string;
  operatorVariant?: 'v1' | 'v2';
  onRemove?: (id?: string) => void;
  onRemoveSelection?: (selectionId: number) => void;
  variant?: 'v1' | 'v2';
  isResponsive?: boolean;
  icon?: React.ReactNode;
  classNames?: string;
  onClick?: (element: HTMLElement | null, id?: string) => void;
  clickable?: boolean;
  disabled?: boolean;
  dataTestId: string;
}

const ArrFilterChip: React.FC<FilterChipProps> = ({
  id = '',
  filterName,
  selections,
  operator,
  operatorVariant = 'v1',
  onRemove,
  onRemoveSelection,
  variant = 'v1',
  isResponsive = false,
  icon,
  classNames = '',
  onClick,
  clickable = false,
  disabled = false,
  dataTestId,
  ...chipProps
}) => {
  const v1ChipClass = clsx(
    `arr-filter-chip-v1 ${disabled && 'disabled'}`,
    classNames
  );
  const v2ChipClass = clsx(
    `arr-filter-chip-v1-v2-responsive ${disabled && 'disabled'}`,
    classNames
  );
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const parentContainerRef = useRef<HTMLElement | null>(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const [selectionData, setSelectionData] = useState<Selection[]>([]);
  const chipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setSelectionData([...selections]);
  }, [selections]);

  const onOperatorClick = (selectionId: number | string) => {
    if (disabled) return;
    setSelectionData(
      selectionData.filter((data: Selection) => data.id !== selectionId)
    );
    if (onRemoveSelection && typeof selectionId === 'number') {
      onRemoveSelection(selectionId);
    }
    checkScroll();
  };

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const {scrollLeft, scrollWidth, clientWidth} = container;
      setShowLeftButton(scrollLeft > 0);
      setShowRightButton(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };

  const handleKeyDownForRemove = (
    event: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (disabled) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      if (onRemove) onRemove(id);
    }
  };

  const handleKeyDownForRemoveSelection = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    selectionId: number | string
  ) => {
    if (disabled) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      if (onRemoveSelection && typeof selectionId === 'number') {
        onRemoveSelection(selectionId);
      }
    }
  };

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    const handleResize = () => checkScroll();
    if (container) {
      container.addEventListener('scroll', handleResize);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleResize);
      }
    };
  }, []);

  const handleScroll = (direction: 'left' | 'right') => {
    if (disabled) return;
    if (scrollContainerRef.current) {
      const scrollAmount = 150;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  if (variant === 'v1' && !isResponsive) {
    return (
      <Box className={v1ChipClass} ref={parentContainerRef}>
        {(icon || filterName) && (
          <Chip
            ref={chipRef}
            label={
              <div
                className={`arr-filter-chip-v1__filtername`}
                data-testid={`arr-filter-chip-div-${dataTestId}`}
              >
                {icon}
                <Typography
                  className="typo-text label3_size"
                  data-testid={`arr-filter-chip-filter-name-${dataTestId}`}
                >
                  {filterName !== 'Search' ? filterName : ''}
                </Typography>
              </div>
            }
            className="arr-filter-chip-v1__filter"
            disabled={disabled}
            {...chipProps}
            {...(clickable && !disabled
              ? {
                  tabIndex: 0,
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    onClick?.(chipRef.current || e.currentTarget, id); // <-- pass actual chip DOM
                  },
                  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      onClick?.(chipRef.current || e.currentTarget, id); // <-- pass actual chip DOM
                    }
                  },
                }
              : {})}
          />
        )}
        {showLeftButton && (
          <ArrIconButton
            icon={<ChevronLeft />}
            onClick={() => handleScroll('left')}
            className="arr-filter-chip__scrollButton"
            ariaLabel={`${t('common.filterChip.scrollLeft')}`}
            disabled={disabled}
            testId="scroll-left"
          />
        )}
        <Box
          className="arr-filter-chip-v1-selection-block"
          ref={scrollContainerRef}
          onScroll={checkScroll}
          data-testid="scroll-container"
        >
          {selectionData?.map((selection, index) => (
            <React.Fragment key={selection.id}>
              <Chip
                ref={chipRef}
                className={`arr-filter-chip-v1-selection ${!filterName && !icon && index === 0 ? 'extra-padding' : ''}`}
                label={
                  <Typography
                    className="typo-text body4_size"
                    data-testid={`arr-filter-chip-value-${dataTestId}-${index}`}
                  >
                    {selection.value}
                  </Typography>
                }
                size="small"
                {...(clickable
                  ? {
                      tabIndex: 0,
                      onClick: (e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        onClick?.(chipRef.current || e.currentTarget, id); // <-- pass actual chip DOM
                      },
                      onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          onClick?.(chipRef.current || e.currentTarget, id); // <-- pass actual chip DOM
                        }
                      },
                    }
                  : {})}
              />
              {index < selectionData.length - 1 && (
                <Chip
                  ref={chipRef}
                  label={
                    <>
                      <Typography
                        className="typo-text body4_size"
                        data-testid={`arr-filter-chip-operator-${dataTestId}-${index}`}
                      >
                        {operator}
                      </Typography>
                      {operatorVariant === 'v2' ? (
                        <ArrIconButton
                          testId={`close-${selection.id}`}
                          icon={<CloseIcon />}
                          onClick={() => onOperatorClick(selection.id)}
                          ariaLabel={`${t('common.filterChip.clickToClear')} ${filterName} ${t('common.filterChip.filter')}`}
                          shape="none"
                          size="small"
                          className="chip-close-icon"
                          data-testid={`${selection.id}-clear-button`}
                          onKeyDown={(e) =>
                            handleKeyDownForRemoveSelection(e, selection.id)
                          }
                          disabled={disabled}
                        />
                      ) : null}
                    </>
                  }
                  className="arr-filter-chip-v1__operator"
                  size="small"
                />
              )}
            </React.Fragment>
          ))}
        </Box>
        {showRightButton && (
          <ArrIconButton
            icon={<ChevronRight />}
            onClick={() => handleScroll('right')}
            className="arr-filter-chip__scrollButton"
            ariaLabel={`${t('common.filterChip.scrollRight')}`}
            disabled={disabled}
            testId="scroll-right"
          />
        )}
        {!disabled && (
          <Box className="arr-filter-chip-v1__close-block">
            <ArrIconButton
              icon={<CloseIcon />}
              onClick={() => {
                if (onRemove) onRemove(id);
              }}
              ariaLabel={`${t('common.filterChip.clickToClear')} ${filterName} ${t('common.filterChip.filter')}`}
              shape="none"
              size="small"
              className="chip-close-icon"
              testId={`${(dataTestId ?? id) ? `${dataTestId ?? id}-` : ''}remove-button`}
              tabIndex={0}
              onKeyDown={handleKeyDownForRemove}
            />
          </Box>
        )}
      </Box>
    );
  } else {
    return (
      <Box
        className={`arr-filter-chip-v1-v2-responsive ${v2ChipClass}`}
        ref={parentContainerRef}
      >
        <Chip
          label={
            <>
              <Box
                ref={chipRef}
                className="arr-filter-chip-filter-selection-continer"
                {...(clickable && !disabled
                  ? {
                      tabIndex: 0,
                      onClick: (e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        onClick?.(chipRef.current || e.currentTarget, id);
                      },
                      onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          onClick?.(chipRef.current || e.currentTarget, id);
                        }
                      },
                    }
                  : {})}
              >
                <Typography
                  data-testid={`filter-value-${isArray(filterName) ? filterName[0] : filterName}`}
                >
                  <span
                    className={
                      variant === 'v2'
                        ? 'typo-text label3_size'
                        : 'typo-text label4_size'
                    }
                    data-testid={`arr-filter-chip-span-v4-${dataTestId}`}
                  >
                    {isArray(filterName)
                      ? `${filterName.map((name) => `${name}`).join(' | ')}: `
                      : `${filterName} | Include: `}
                  </span>
                  {selectionData?.length > 0 && (
                    <span
                      className={
                        variant === 'v2'
                          ? 'typo-text body4_size arr-filter-chip-filter__selection-block'
                          : 'typo-text body5_size arr-filter-chip-filter__selection-block'
                      }
                      data-testid={`arr-filter-chip-span-value-v4-${dataTestId}`}
                    >
                      {selectionData
                        .map((selection) => `${selection.value}`)
                        .join(operator ? ` ${operator} ` : ', ')}
                    </span>
                  )}
                </Typography>
              </Box>
              {!disabled && (
                <ArrIconButton
                  icon={<CloseIcon />}
                  onClick={() => {
                    if (onRemove) onRemove(id);
                  }}
                  ariaLabel="clear"
                  shape="none"
                  size="small"
                  className="chip-close-icon"
                  testId={`${(dataTestId ?? id) ? `${dataTestId ?? id}-` : ''}clear-button`}
                  onKeyDown={handleKeyDownForRemove}
                />
              )}
            </>
          }
        />
      </Box>
    );
  }
};

export default ArrFilterChip;
